/** Microphone capture -> 16 kHz mono PCM16 frames of `frameMs`. */

import { floatToPcm16, levels, StreamResampler } from "./pcm.ts";

export type CaptureOptions = {
  deviceId?: string;
  frameMs?: number;
  processing: { echoCancellation: boolean; noiseSuppression: boolean; autoGainControl: boolean };
  workletUrl: string;
  onFrame: (pcm: ArrayBuffer) => void;
  onLevel: (db: number, peak: number) => void;
  onEnded: (reason: string) => void;
};

export type Capture = {
  deviceRate: number;
  path: "native-16k" | "resampled";
  label: string;
  stop: () => Promise<void>;
};

export class CaptureError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function openStream(options: CaptureOptions): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new CaptureError(
      "unsupported",
      window.isSecureContext
        ? "This browser cannot capture audio."
        : "Microphone access needs HTTPS (or localhost).",
    );
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        ...options.processing,
        ...(options.deviceId ? { deviceId: { exact: options.deviceId } } : {}),
      },
      video: false,
    });
  } catch (error) {
    const name = error instanceof DOMException ? error.name : "";
    if (name === "NotAllowedError" || name === "SecurityError") {
      throw new CaptureError("denied", "Microphone permission was denied. Allow it in the address bar, then start again.");
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") {
      throw new CaptureError("no-device", "No usable microphone was found. Check the input device in Settings.");
    }
    if (name === "NotReadableError") {
      throw new CaptureError("busy", "The microphone is in use by another application.");
    }
    throw new CaptureError("mic", "The microphone could not be opened.");
  }
}

export async function startCapture(options: CaptureOptions): Promise<Capture> {
  const frameMs = options.frameMs ?? 80;
  const stream = await openStream(options);
  const track = stream.getAudioTracks()[0];

  // Prefer a 16 kHz graph: the browser's resampler is good and cheap. Some
  // engines (Firefox) refuse to connect a mic to a context at another rate.
  let context: AudioContext;
  let source: MediaStreamAudioSourceNode;
  let resampler: StreamResampler | null = null;
  try {
    context = new AudioContext({ sampleRate: 16000, latencyHint: "interactive" });
    source = context.createMediaStreamSource(stream);
  } catch {
    context = new AudioContext({ latencyHint: "interactive" });
    source = context.createMediaStreamSource(stream);
    resampler = new StreamResampler(context.sampleRate, 16000);
  }

  try {
    if (context.state === "suspended") await context.resume();
    await context.audioWorklet.addModule(options.workletUrl);
  } catch {
    stream.getTracks().forEach((t) => t.stop());
    await context.close().catch(() => undefined);
    throw new CaptureError("unsupported", "This browser does not support AudioWorklet capture.");
  }

  const node = new AudioWorkletNode(context, "interligne-capture", {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    processorOptions: { frameSize: Math.round((context.sampleRate * frameMs) / 1000) },
  });
  const mute = context.createGain();
  mute.gain.value = 0;
  node.port.onmessage = (event: MessageEvent<Float32Array>) => {
    const frame = event.data;
    const { db, peak } = levels(frame);
    options.onLevel(db, peak);
    options.onFrame(floatToPcm16(resampler ? resampler.process(frame) : frame));
  };
  source.connect(node);
  node.connect(mute);
  mute.connect(context.destination);

  let stopped = false;
  if (track) {
    track.onended = () => {
      if (!stopped) options.onEnded("The microphone was disconnected.");
    };
  }
  context.onstatechange = () => {
    // iOS and some Android browsers suspend audio when interrupted.
    if (!stopped && context.state === "suspended") void context.resume().catch(() => undefined);
  };

  return {
    deviceRate: context.sampleRate,
    path: resampler ? "resampled" : "native-16k",
    label: track?.label ?? "",
    stop: async () => {
      if (stopped) return;
      stopped = true;
      node.port.onmessage = null;
      stream.getTracks().forEach((t) => t.stop());
      source.disconnect();
      node.disconnect();
      mute.disconnect();
      await context.close().catch(() => undefined);
    },
  };
}

export async function listMicrophones(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const all = await navigator.mediaDevices.enumerateDevices();
  return all.filter((d) => d.kind === "audioinput");
}

/** Keep the screen awake while interpreting (released automatically when hidden). */
export class ScreenWake {
  private lock: { release(): Promise<void> } | null = null;
  private wanted = false;
  private readonly onVisible = () => {
    if (this.wanted && document.visibilityState === "visible") void this.acquire();
  };

  async enable(): Promise<void> {
    this.wanted = true;
    document.addEventListener("visibilitychange", this.onVisible);
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.wanted = false;
    document.removeEventListener("visibilitychange", this.onVisible);
    const lock = this.lock;
    this.lock = null;
    await lock?.release().catch(() => undefined);
  }

  private async acquire(): Promise<void> {
    const wake = (navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<{ release(): Promise<void> }> } }).wakeLock;
    if (!wake) return;
    try {
      this.lock = await wake.request("screen");
    } catch {
      this.lock = null;
    }
  }
}
